import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { StarIcon, CalendarDaysIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { PortraitFrame } from '../common/editorial/EditorialControls';

const SPECIALTIES = ['All', 'Retirement Planning', 'Tax Strategy', 'Investment Management', 'Debt Reduction', 'Estate Planning'];

const CredentialBadge = ({ cred }) => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-label font-medium bg-pine-tint text-pine border border-hairline mr-1">
    {cred}
  </span>
);

const AdvisorCard = ({ advisor }) => {
  const navigate = useNavigate();
  return (
    <article
      className="aura-card cursor-pointer hover:bg-pine-tint/30 transition-colors duration-300"
      onClick={() => navigate(`/advisors/${advisor.user_id}`)}
      onKeyDown={(e) => e.key === 'Enter' && navigate(`/advisors/${advisor.user_id}`)}
      role="button"
      tabIndex={0}
    >
      <div className="flex items-start gap-4 mb-4">
        <PortraitFrame
          src={advisor.photo_url}
          alt={advisor.name}
          initials={advisor.name?.[0]?.toUpperCase() || '?'}
          size="sm"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-h3 text-primary truncate">{advisor.name}</h3>
            <ShieldCheckIcon className="w-4 h-4 text-pine shrink-0" title="Verified advisor" />
          </div>
          <div className="flex flex-wrap mt-1">
            {(advisor.credentials || []).map((c) => (
              <CredentialBadge key={c} cred={c} />
            ))}
          </div>
        </div>
      </div>
      <p className="text-body text-secondary mb-4 leading-relaxed line-clamp-2">{advisor.bio_snippet}</p>
      <div className="flex flex-wrap gap-1 mb-4">
        {(advisor.specialties || []).slice(0, 3).map((s) => (
          <span key={s} className="text-label px-2 py-0.5 border border-hairline rounded-md text-secondary">
            {s}
          </span>
        ))}
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-hairline">
        <span className="text-label text-secondary">
          <StarIcon className="w-4 h-4 inline text-pine mr-1" />
          <span className="figure">{advisor.years_experience}</span> yrs exp
        </span>
        <div className="flex items-center gap-3">
          {advisor.hourly_rate && (
            <span className="figure text-body text-primary">${advisor.hourly_rate}/hr</span>
          )}
          <button
            type="button"
            className="btn-secondary text-label py-1.5 px-3 flex items-center gap-1"
            onClick={(e) => {
              e.stopPropagation();
              if (advisor.booking_url) window.open(advisor.booking_url, '_blank');
            }}
          >
            <CalendarDaysIcon className="w-4 h-4" />
            Book
          </button>
        </div>
      </div>
    </article>
  );
};

const AdvisorMarketplace = () => {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('All');
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get('/api/community/advisors')
      .then((r) => setAdvisors(r.data.advisors || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered =
    activeFilter === 'All' ? advisors : advisors.filter((a) => (a.specialties || []).includes(activeFilter));

  return (
    <div className="min-h-screen bg-bone p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="mb-8 pb-6 border-b border-hairline"
        >
          <h1 className="font-display text-h1 text-primary mb-2">Find a financial advisor</h1>
          <p className="text-body text-secondary">
            Connect with verified CFPs, CFAs, and RIAs — no $100k minimum required.
          </p>
        </motion.header>

        <div className="flex flex-wrap gap-2 mb-8">
          {SPECIALTIES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setActiveFilter(s)}
              className={`px-4 py-2 rounded-md text-label font-medium border transition-colors duration-300 ${
                activeFilter === s
                  ? 'bg-pine-tint border-pine text-pine'
                  : 'border-hairline text-secondary hover:text-primary hover:bg-pine-tint/50'
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-body text-secondary py-16 text-center">Loading advisors…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 aura-card">
            <p className="text-body text-secondary mb-4">No advisors found for this specialty yet.</p>
            <button type="button" onClick={() => navigate('/advisors/apply')} className="btn-secondary">
              Be the first to apply
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((a, i) => (
              <motion.div
                key={a.user_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05, duration: 0.4, ease: 'easeOut' }}
              >
                <AdvisorCard advisor={a} />
              </motion.div>
            ))}
          </div>
        )}

        <section className="mt-12 pt-8 border-t border-hairline text-center">
          <h2 className="font-display text-h2 text-primary mb-2">Are you a financial professional?</h2>
          <p className="text-body text-secondary mb-4 max-w-lg mx-auto">
            Join our network of verified advisors and help day-2 investors take the right next step.
          </p>
          <button type="button" onClick={() => navigate('/advisors/apply')} className="btn-primary">
            Apply to join
          </button>
        </section>
      </div>
    </div>
  );
};

export default AdvisorMarketplace;
